import { ADMIN_BASE, call, query } from './client.ts'
import { TENANT_BASE } from './tenant.ts'
import type { DetectionSeverity } from '../sse/types.ts'

/**
 * Análise por IA (§4.11 admin, §5.12 cliente).
 *
 * Os tipos espelham `AiFindingPayloads.cs` campo a campo, em snake_case. Os
 * vocabulários fechados (status, veredito, ação) ficam como união literal, mas
 * quem lê trata valor desconhecido: o servidor pode ganhar um termo antes do
 * Hermes.
 *
 * Tudo que é texto aqui (`hypothesis`, `narrative`, `explanation`,
 * `failure_reason`, linhas de comando, caminhos) pode ter sido escrito pelo
 * atacante. Só vira texto puro na tela — nunca HTML, markdown ou link.
 */

export type AiFindingStatus =
  | 'triage_only'
  | 'awaiting_analysis'
  | 'analyzed'
  | 'analysis_failed'
  | 'confirmed'
  | 'dismissed'

export type AiVerdict = 'attack' | 'suspicious' | 'benign' | 'inconclusive'

export type AiRecommendedAction = 'none' | 'monitor' | 'collect_artifact' | 'isolate_host' | 'escalate'

export type AiDecision = 'confirmed' | 'dismissed'

/** `AiFindingSignalResponse`. */
export interface AiFindingSignal {
  code: string
  weight: number
  explanation: string
  event_keys: string[]
}

/** `AiFindingResponse`. Campos do veredito são nulos fora de analyzed/confirmed/dismissed. */
export interface AiFinding {
  id: string
  tenant_id: string
  endpoint_id: string
  host: string
  analysis_run_id: string
  window_start: string
  window_end: string
  status: AiFindingStatus
  triage_score: number
  triage_signals: AiFindingSignal[]
  selected_for_analysis: boolean
  verdict: AiVerdict | null
  severity: DetectionSeverity | null
  /** 0 a 1. */
  confidence: number | null
  hypothesis: string | null
  narrative: string | null
  mitre_techniques: string[]
  recommended_action: AiRecommendedAction | null
  evidence_event_keys: string[]
  analyzer: string | null
  prompt_version: string | null
  contract_version: string | null
  tokens_in: number | null
  tokens_out: number | null
  failure_reason: string | null
  related_finding_id: string | null
  created_at: string
  updated_at: string
}

/** `AiFindingPageResponse`. Sem total: pagina por `next_cursor`. */
export interface AiFindingPage {
  items: AiFinding[]
  page_size: number
  has_more: boolean
  next_cursor: string | null
  order: string
}

/** `AiFindingEventResponse`. `log_id` abre o detalhe do evento. */
export interface AiFindingEvent {
  log_id: number
  event_key: string
  event_time: string
  event_type: string
  action: string | null
  process_name: string | null
  process_pid: number | null
  process_command_line: string | null
  user_name: string | null
  file_path: string | null
  registry_path: string | null
  dns_question: string | null
  dst_ip: string | null
  dst_port: number | null
  /** `evidence` e/ou `signal:<código>`. */
  cited_as: string[]
}

/** `AiFindingDetailResponse`. */
export interface AiFindingDetail {
  finding: AiFinding
  events: AiFindingEvent[]
  /** A busca parou no teto de leitura da janela: pode faltar evento. */
  events_truncated: boolean
}

/** `EventAiFindingResponse`. `cited_as` vazio = na janela, sem ser citado. */
export interface EventAiFinding {
  finding: AiFinding
  cited_as: string[]
}

/** `EventAiFindingsResponse`. `findings` vazio = janela não analisada ou sem sinal. */
export interface EventAiFindings {
  log_id: number
  event_key: string
  tenant_id: string
  endpoint_id: string
  event_time: string
  findings: EventAiFinding[]
}

export interface AiFindingQuery {
  /** Só na rota admin. A fonte de cliente descarta. */
  tenantId?: string
  endpointId?: string
  status?: string
  verdict?: string
  from?: string
  to?: string
  cursor?: string | null
  pageSize?: number
}

function listPath(params: AiFindingQuery, withTenant: boolean): string {
  return `/ai-findings${query({
    tenantId: withTenant ? params.tenantId : undefined,
    endpointId: params.endpointId,
    status: params.status,
    verdict: params.verdict,
    from: params.from,
    to: params.to,
    cursor: params.cursor,
    pageSize: params.pageSize,
  })}`
}

export const adminAiFindings = {
  list: (params: AiFindingQuery, signal?: AbortSignal) =>
    call<AiFindingPage>(listPath(params, true), { base: ADMIN_BASE, signal }),

  detail: (id: string, signal?: AbortSignal) =>
    call<AiFindingDetail>(`/ai-findings/${encodeURIComponent(id)}`, { base: ADMIN_BASE, signal }),

  forLog: (logId: number, signal?: AbortSignal) =>
    call<EventAiFindings>(`/logs/${logId}/ai-findings`, { base: ADMIN_BASE, signal }),

  /** A IA sugere; o analista decide. Devolve o achado com o novo `status`. */
  decide: (id: string, decision: AiDecision) =>
    call<AiFinding>(`/ai-findings/${encodeURIComponent(id)}/decision`, {
      base: ADMIN_BASE,
      method: 'POST',
      body: { decision },
    }),
}

/** Cliente: somente leitura, sem `tenantId` — a empresa é a do token. */
export const tenantAiFindings = {
  list: (params: AiFindingQuery, signal?: AbortSignal) =>
    call<AiFindingPage>(listPath(params, false), { base: TENANT_BASE, signal }),

  detail: (id: string, signal?: AbortSignal) =>
    call<AiFindingDetail>(`/ai-findings/${encodeURIComponent(id)}`, { base: TENANT_BASE, signal }),

  forLog: (logId: number, signal?: AbortSignal) =>
    call<EventAiFindings>(`/logs/${logId}/ai-findings`, { base: TENANT_BASE, signal }),
}
